<script>
import { get } from "lodash";

export default {
  name: "StandardSubscription",
  props: {
    standardSubscription: {
      type: Object,
      required: true,
    },
    modifySubscriptionPeriodText: {
      type: Function,
      required: true
    },
    convertDate: {
      type: Function,
      required: true
    },
    changeStandardSubscriptionSettings: {
      type: Function,
      required: true
    },
    teams: {
      type: Array,
      required: true
    },
    turnOffPayingBySponsorForGiftSubscription: {
      type: Function,
      required: true
    }
  },
  data() {
    return {}
  },
  computed: {
    title() {
      return get(this.standardSubscription, 'title', '');
    },
    price() {
      return get(this.standardSubscription, 'price', 0);
    },
    currency() {
      return get(this.standardSubscription, 'currency', '');
    },
    period() {
      return get(this.standardSubscription, 'period', '');
    },
    active() {
      return this.standardSubscription.active;
    },
    hasNextSubscription() {
      return this.standardSubscription.next_subscription !== null;
    },
    subscriptionStatusBlocked() {
      return this.active === false || this.isPayingBySponsor;
    },
    isPayingBySponsor() {
      return get(this.standardSubscription, 'sponsored_subscription_slot.sponsored_subscription.is_paying_by_sponsor') === true;
    },
    subscriptionStatus() {
      const has_next_subscription = get(this.standardSubscription, 'next_subscription');
      if (has_next_subscription !== null) {
        return !!this.standardSubscription.is_allowed_next_subscription;
      }
      const is_allowed_autorenew = get(this.standardSubscription, 'is_allowed_autorenew');
      if (is_allowed_autorenew !== null) {
        return !!this.standardSubscription.autorenew;
      }
    },
    subscriptionType() {
      if (get(this.standardSubscription, 'is_team_subscription', false)) {
        return 'team';
      }
      return this.standardSubscription.type;
    },
    teamName() {
      return get(this.teams, '[0].name');
    },
    sponsorEmail() {
      if (this.subscriptionType !== 'gift') {
        return null;
      }
      const sponsor_email = get(this.standardSubscription, 'sponsored_subscription_slot.sponsored_subscription.sponsor.email');
      if (!sponsor_email) {
        return null;
      }
      const first_name = get(this.standardSubscription, 'sponsored_subscription_slot.sponsored_subscription.sponsor.first_name');
      const last_name = get(this.standardSubscription, 'sponsored_subscription_slot.sponsored_subscription.sponsor.last_name');
      if (first_name && last_name) {
        return `${first_name} ${last_name} (${sponsor_email})`;
      }
      return sponsor_email;
    },
  },
  methods: {
    getSubscriptionStatusSettings(value) {
      const has_next_subscription = get(this.standardSubscription, 'next_subscription');
      if (has_next_subscription !== null) {
        return {is_allowed_next_subscription: value};
      }
      const is_allowed_autorenew = get(this.standardSubscription, 'is_allowed_autorenew');
      if (is_allowed_autorenew !== null) {
        return {autorenew: value};
      }
    }
  }
}
</script>

<template>
  <div>

    <b-row>
      <b-col cols="5">
        <p class="text-secondary mb-1">Membership</p>
      </b-col>
      <b-col cols="7">
        <p class="mb-1" style="font-weight: 600;">{{ title }}</p>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="5">
        <p class="text-secondary mb-1">Terms</p>
      </b-col>
      <b-col cols="7">
        <p class="mb-1" v-if="price === 0">Free</p>
        <p class="mb-1" v-else>
          {{ (price / 100).toFixed(2) }}
          {{ currency }} / {{ modifySubscriptionPeriodText(period) }}
        </p>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="5">
        <p class="text-secondary mb-1">Active</p>
      </b-col>
      <b-col cols="7">
        <p class="mb-1">
          <span v-if="active" class="text-success text-uppercase">Yes</span>
          <span v-else class="text-uppercase">No</span>
        </p>
      </b-col>
    </b-row>
    <b-row v-if="standardSubscription.subscription_start_date">
      <b-col cols="5">
        <p class="text-secondary mb-1">Created</p>
      </b-col>
      <b-col cols="7">
        <p class="mb-1">
          {{ convertDate(standardSubscription.subscription_start_date) }}
        </p>
      </b-col>
    </b-row>
    <b-row v-if="subscriptionType === 'gift'">
      <b-col cols="5">
        <p class="text-secondary mb-1">Activated</p>
      </b-col>
      <b-col cols="7">
        <p class="mb-1">
          {{ convertDate(standardSubscription.sponsored_subscription_slot.activated_at) }}
        </p>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="5">
        <p class="text-secondary mb-1">Expires</p>
      </b-col>
      <b-col cols="7">
        <p class="mb-1">
          {{ convertDate(standardSubscription.subscription_end_date) }}
        </p>
      </b-col>
    </b-row>

    <b-row v-if="subscriptionType === 'standard'">
      <b-col cols="5">
        <p class="text-secondary mb-1">Subscription status</p>
      </b-col>
      <b-col cols="7" class="d-flex flex-row align-items-center switch" :class="{'blocked': subscriptionStatusBlocked}">
        <b-btn
          :disabled="subscriptionStatusBlocked || subscriptionStatus"
          @click="changeStandardSubscriptionSettings(standardSubscription.id, getSubscriptionStatusSettings(true))"
          variant="link"
          class="p-0 text-uppercase pb-1"
          :class="{'off': !subscriptionStatus, 'on': subscriptionStatus}"
        >
          <small class="flex-shrink-0">
            <span :class="{'text-success': !subscriptionStatusBlocked && subscriptionStatus}">Auto-renew</span>
          </small>
        </b-btn>
        <span class="mx-1">|</span>
        <b-btn
          :disabled="subscriptionStatusBlocked || !subscriptionStatus"
          @click="changeStandardSubscriptionSettings(standardSubscription.id, getSubscriptionStatusSettings(false))"
          variant="link"
          class="p-0 text-uppercase pb-1"
          :class="{'off': subscriptionStatus, 'on': !subscriptionStatus}"
        >
          <small class="flex-shrink-0">
            <span :class="{'text-success': !subscriptionStatusBlocked && !subscriptionStatus}">Cancel</span>
          </small>
        </b-btn>
      </b-col>
    </b-row>

    <b-row v-if="subscriptionType === 'team'">
      <b-col cols="5">
        <p class="text-secondary mb-1">Subscription status</p>
      </b-col>
      <b-col cols="7">
        <p class="mb-1">
          <template v-if="teamName">
            {{ teamName }}<br>(group subscription)
          </template>
          <template v-else>
            group subscription
          </template>
        </p>
      </b-col>
    </b-row>

    <b-row v-if="subscriptionType === 'gift'">
      <b-col cols="5">
        <p class="text-secondary mb-1">Subscription status</p>
      </b-col>
      <b-col cols="7" class="d-flex flex-row align-items-center switch" :class="{'blocked': subscriptionStatusBlocked}">
        <b-btn
          :disabled="subscriptionStatusBlocked || subscriptionStatus"
          @click="changeStandardSubscriptionSettings(standardSubscription.id, getSubscriptionStatusSettings(true))"
          variant="link"
          class="p-0 text-uppercase pb-1"
          :class="{'off': !subscriptionStatus, 'on': subscriptionStatus}"
        >
          <small class="flex-shrink-0">
            <span :class="{'text-success': subscriptionStatus}">Auto-renew</span>
          </small>
        </b-btn>
        <span class="mx-1">|</span>
        <b-btn
          :disabled="subscriptionStatusBlocked || !subscriptionStatus"
          @click="changeStandardSubscriptionSettings(standardSubscription.id, getSubscriptionStatusSettings(false))"
          variant="link"
          class="p-0 text-uppercase pb-1"
          :class="{'off': subscriptionStatus, 'on': !subscriptionStatus}"
        >
          <small class="flex-shrink-0">
            <span :class="{'text-success': !subscriptionStatusBlocked && !subscriptionStatus}">Cancel</span>
          </small>
        </b-btn>
      </b-col>
    </b-row>

    <b-row v-if="subscriptionType === 'gift'">
      <b-col cols="5">
        <p class="text-secondary mb-1">Paid by sponsor</p>
      </b-col>
      <b-col cols="7" class="d-flex flex-row align-items-center switch" >
        <b-btn
          :disabled="true"
          variant="link"
          class="p-0 text-uppercase pb-1 blocked"
          :class="{'off': !isPayingBySponsor, 'on': isPayingBySponsor}"
        >
          <small class="flex-shrink-0">
            <u v-if="!isPayingBySponsor">On</u>
            <span class="text-success" v-else>On</span>
          </small>
        </b-btn>
        <span class="mx-1">|</span>
        <b-btn
          :disabled="!isPayingBySponsor"
          @click="turnOffPayingBySponsorForGiftSubscription(standardSubscription.sponsored_subscription_slot.id)"
          variant="link"
          class="p-0 text-uppercase pb-1"
          :class="{'off': isPayingBySponsor, 'on': !isPayingBySponsor, 'blocked': !isPayingBySponsor}"
        >
          <small class="flex-shrink-0">
            <u v-if="isPayingBySponsor">Off</u>
            <span class="text-success" v-else>Off</span>
          </small>
        </b-btn>
      </b-col>
    </b-row>

    <b-row v-if="sponsorEmail">
      <b-col cols="5">
        <p class="text-secondary mb-1">Sponsor</p>
      </b-col>
      <b-col cols="7">
        <p class="mb-0">{{ sponsorEmail }}</p>
      </b-col>
    </b-row>

  </div>
</template>



// WEBPACK FOOTER //
// src/components/StandardSubscription.vue